import { Link, type Href } from 'expo-router';
import { Platform, Pressable, StyleSheet, View } from 'react-native';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Container, Text } from '@/components/ui';
import { useTheme } from '@/theme';

type LinkItem = { label: string; href?: Href; external?: boolean; static?: boolean };
type Column = { title: string; items: LinkItem[]; socials?: string[] };

export function Footer() {
  const theme = useTheme();
  const { t } = useTranslation();

  const cols: Column[] = [
    {
      title: t('common.footer.kurseTitle'),
      items: [
        { label: t('common.footer.modulA'), href: '/kurse' },
        { label: t('common.footer.modulB'), href: '/kurse' },
        { label: t('common.footer.openLevel'), href: '/kurse' },
        { label: t('common.footer.sambaGafieira'), href: '/kurse' },
        { label: t('common.footer.privateLink'), href: '/kurse' },
      ],
    },
    {
      title: t('common.footer.brandColTitle'),
      items: [
        { label: t('common.footer.eventsLink'), href: '/events' },
        { label: t('common.footer.ticketsLink'), href: '/kontakt' },
        { label: t('common.footer.vereinLink'), external: true },
      ],
    },
    {
      title: t('common.footer.studioLabel'),
      items: [
        { label: t('common.footer.addressMultiline'), static: true },
        { label: t('common.utility.email'), static: true },
        { label: t('common.utility.phone'), static: true },
      ],
      socials: [
        t('common.footer.instagram'),
        t('common.footer.facebook'),
        t('common.footer.youtube'),
      ],
    },
  ];

  return (
    <View style={[styles.outer, { borderTopColor: theme.colors.rule, backgroundColor: theme.colors.bg }]}>
      <Container>
        <View style={styles.grid}>
          {/* Brand block */}
          <View style={styles.brandBlock}>
            <Text
              style={{
                fontFamily: theme.fontFamily.display,
                fontSize: 56,
                lineHeight: 50,
                letterSpacing: -1,
                color: theme.colors.fg,
              }}
            >
              Zouk
              <Text
                style={{
                  fontFamily: theme.fontFamily.displayItalic,
                  fontStyle: 'italic',
                  color: theme.colors.accent,
                  fontSize: 56,
                }}
              >
                ess
              </Text>
              ence
            </Text>
            <Text
              style={{
                fontFamily: theme.fontFamily.body as string,
                fontSize: 14,
                lineHeight: 22,
                color: theme.colors.fgSoft,
                marginTop: 14,
                maxWidth: 360,
              }}
            >
              {t('common.footer.brandTagline')}
            </Text>
          </View>

          {/* Link columns */}
          {cols.map((col) => (
            <View key={col.title} style={styles.col}>
              <Text
                style={[
                  styles.h5,
                  {
                    color: theme.colors.fgMute,
                    fontFamily: theme.fontFamily.mono as string,
                  },
                ]}
              >
                {col.title}
              </Text>
              <View style={styles.ul}>
                {col.items.map((it, i) => (
                  <FooterLink key={col.title + i} item={it} />
                ))}
              </View>
              {col.socials ? (
                <View style={styles.socials}>
                  {col.socials.map((s) => (
                    <FooterLink key={s} item={{ label: s, external: true }} />
                  ))}
                </View>
              ) : null}
            </View>
          ))}
        </View>

        <View style={[styles.bottom, { borderTopColor: theme.colors.rule }]}>
          <Text
            style={[
              styles.bottomText,
              { color: theme.colors.fgMute, fontFamily: theme.fontFamily.mono as string },
            ]}
          >
            {t('common.footer.copyright', { year: new Date().getFullYear() })}
          </Text>
          <View style={styles.bottomLinks}>
            {[
              { key: 'agb', label: t('common.footer.agb') },
              { key: 'privacy', label: t('common.footer.privacy') },
              { key: 'cookies', label: t('common.footer.cookies') },
            ].map((it) => (
              <FooterLink key={it.key} item={{ label: it.label, external: true }} mono />
            ))}
          </View>
        </View>
      </Container>
    </View>
  );
}

function FooterLink({ item, mono }: { item: LinkItem; mono?: boolean }) {
  const theme = useTheme();
  const [hovered, setHovered] = useState(false);

  const color = hovered ? theme.colors.accent : item.static ? theme.colors.fg : theme.colors.fg;
  const fontFamily = mono ? (theme.fontFamily.mono as string) : (theme.fontFamily.body as string);
  const fontSize = mono ? 11 : 15;
  const letterSpacing = mono ? 1.5 : 0;
  const textTransform = mono ? ('uppercase' as const) : ('none' as const);
  const lineHeight = mono ? 16 : 22;

  if (item.static || !item.href) {
    return (
      <Pressable
        accessibilityRole={item.static ? undefined : 'link'}
        {...(Platform.OS === 'web' && !item.static
          ? { onHoverIn: () => setHovered(true), onHoverOut: () => setHovered(false) }
          : {})}
        style={styles.linkPress}
      >
        <Text
          style={[
            {
              color: item.static ? theme.colors.fg : color,
              fontFamily,
              fontSize,
              letterSpacing,
              textTransform,
              lineHeight,
            },
            Platform.OS === 'web'
              ? ({ transitionProperty: 'color', transitionDuration: '160ms' } as any)
              : null,
          ]}
        >
          {item.label}
        </Text>
      </Pressable>
    );
  }

  return (
    <Link href={item.href} asChild>
      <Pressable
        accessibilityRole="link"
        {...(Platform.OS === 'web'
          ? { onHoverIn: () => setHovered(true), onHoverOut: () => setHovered(false) }
          : {})}
        style={styles.linkPress}
      >
        <Text
          style={[
            { color, fontFamily, fontSize, letterSpacing, textTransform, lineHeight },
            Platform.OS === 'web'
              ? ({ transitionProperty: 'color', transitionDuration: '160ms' } as any)
              : null,
          ]}
        >
          {item.label}
        </Text>
      </Pressable>
    </Link>
  );
}

const styles = StyleSheet.create({
  outer: {
    paddingTop: 64,
    paddingBottom: 32,
    borderTopWidth: 1,
    marginTop: 96,
  },
  grid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 48,
  },
  brandBlock: {
    flexBasis: 360,
    flexGrow: 1.6,
  },
  col: {
    flex: 1,
    minWidth: 160,
  },
  h5: {
    fontSize: 11,
    letterSpacing: 1.8,
    textTransform: 'uppercase',
    marginBottom: 18,
  },
  ul: {
    flexDirection: 'column',
    gap: 10,
  },
  linkPress: {
    alignSelf: 'flex-start',
  },
  socials: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 14,
    marginTop: 14,
  },
  bottom: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
    paddingTop: 24,
    marginTop: 64,
    borderTopWidth: 1,
  },
  bottomText: {
    fontSize: 11,
    letterSpacing: 1.6,
    textTransform: 'uppercase',
  },
  bottomLinks: {
    flexDirection: 'row',
    gap: 18,
  },
});
